/**
 * Reconhecer — etapas do fluxo (REC-02, REC-07).
 *
 * São cinco passos (colega, comportamento, tipo e pontos, mensagem, revisão) e quatro
 * estágios no cabeçalho: a revisão continua dentro de "Mensagem".
 *
 * Os índices são os mesmos gravados no rascunho (`bb_recognition_partial__c.step`). Não
 * renumerar: um rascunho em andamento precisa abrir na mesma etapa.
 */
export const STEP = {
  colleague: 0,
  behavior: 1,
  coinPoints: 2,
  message: 3,
  review: 4,
} as const;

export type Step = (typeof STEP)[keyof typeof STEP];

export const STAGE_LABELS = ['Colega', 'Comportamento', 'Tipo e pontos', 'Mensagem'];

export type StageStatus = 'done' | 'current' | 'pending';

const STATUS_READING: Record<StageStatus, string> = {
  done: 'concluída',
  current: 'atual',
  pending: 'pendente',
};

const LAST_STAGE = STAGE_LABELS.length - 1;

export function stageStatus(stage: number, step: Step): StageStatus {
  const currentStage = Math.min(step, LAST_STAGE);

  if (stage === currentStage) return 'current';

  return stage < currentStage ? 'done' : 'pending';
}

/** O que o leitor de tela lê em cada estágio do cabeçalho. */
export function stageReading(stage: number, step: Step) {
  return `Etapa ${stage + 1} de ${STAGE_LABELS.length}: ${STAGE_LABELS[stage]}, ${STATUS_READING[stageStatus(stage, step)]}`;
}

export function primaryActionLabel(step: Step) {
  if (step === STEP.review) return 'Enviar';

  return step === STEP.message ? 'Revisar' : 'Continuar';
}

export type StepChoices = {
  hasTarget: boolean;
  hasBehavior: boolean;
  hasCoin: boolean;
  hasPoints: boolean;
  messageIsValid: boolean;
};

/** A ação principal só libera com a etapa completa. */
export function canAdvance(step: Step, choices: StepChoices) {
  switch (step) {
    case STEP.colleague:
      return choices.hasTarget;
    case STEP.behavior:
      return choices.hasBehavior;
    case STEP.coinPoints:
      return choices.hasCoin && choices.hasPoints;
    default:
      return choices.messageIsValid;
  }
}
